// Each instrument offers the day's main sound and two alternates of the same family; a loop
// picks one per instrument (one undo step per change). Picking plays a short preview. The day's
// atmos kit (twelve pads) is named below. A sound that
// already plays on one of the instrument's extra lanes (grid "+ Lane") can't also be the main one.
import {
  altSound,
  getAtmosKit,
  getPreset,
  instrumentChoices,
  INSTRUMENT_ROLES,
  loopVariant,
  ROLE_SLOT,
  scaleNotesInRange,
  slotRange,
  type InstrumentRole,
} from "@eb/shared";
import { pushNow } from "./audio";
import { setVariant } from "./ops";
import { previewSound } from "./padInput";
import { edit, songKeyOf, useEditor } from "./store";

const ROLE_LABEL: Record<InstrumentRole, string> = {
  bass: "Bass",
  harmony: "Harmony",
  lead: "Lead",
  classic: "Classics",
};

export function SoundPicker() {
  const def = useEditor((s) => s.def);
  const loop = useEditor((s) => s.hist.present);
  const readOnly = useEditor((s) => s.readOnly);
  if (!def) return null;
  const pick = (role: InstrumentRole, variant: number) => {
    const slot = ROLE_SLOT[role];
    edit((l) => setVariant(l, slot, variant));
    pushNow();
    const r = slotRange(slot)!;
    const notes = scaleNotesInRange(songKeyOf(useEditor.getState()) ?? def.key, r.lo, r.hi);
    previewSound(slot, notes[Math.floor(notes.length / 3)] ?? r.lo, 100);
  };
  return (
    <section className="sound-picker panel" aria-label="Instrument sounds" data-testid="sound-picker">
      <h2 className="sound-title">Sounds</h2>
      {INSTRUMENT_ROLES.map((role) => {
        const current = loopVariant(loop, ROLE_SLOT[role]);
        return (
          <div key={role} className="sound-row" role="radiogroup" aria-label={`${ROLE_LABEL[role]} sound`}>
            <span className="sound-role">{ROLE_LABEL[role]}</span>
            {instrumentChoices(def, role).map((id, v) => {
              const p = getPreset(id);
              const onLane = v > 0 && (loop.lanes ?? []).includes(altSound(ROLE_SLOT[role], v));
              return (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={current === v}
                  disabled={readOnly || onLane}
                  className={`chip-btn${current === v ? " on" : ""}`}
                  title={`${p.name} (${p.flavour})${v === 0 ? " · the day's main sound" : ""}${onLane ? " · playing on its own extra lane" : ""}`}
                  data-testid={`sound-${role}-${v}`}
                  onClick={() => pick(role, v)}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        );
      })}
      <p className="sound-kit muted small" data-testid="atmos-kit">
        <span className="sound-role">Atmos kit</span> <strong>{getAtmosKit(def.atmosKit).name}</strong> —{" "}
        {getAtmosKit(def.atmosKit).description}. Twelve pads in the day's key: play them with the pads set to Atmos, or
        in the grid's Atmos lane.
      </p>
    </section>
  );
}
